"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Select as UiSelect,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useBatchTasks } from "../context";
import { isBatchSupportedModel, listBatchModelOptions, type BatchModelOption } from "../helpers/modelHelpers";
import SchemaFormRenderer from "@/features/workspace/schema/SchemaFormRenderer";
import { loadWorkspaceSchema } from "@/features/workspace/modelFormAdapter";
import { initAutoDiscovery } from "@/client/config/models.config";
import {
  deriveInitialValues,
  isDataField,
  traverseFields,
} from "@/features/workspace/schema/helpers";
import type {
  SchemaFormDefinition,
  SchemaFormValues,
} from "@/features/workspace/schema/types";
import type { UploadFileItem } from "@/client/components/FileUpload";

const MIXED_MODEL_VALUE = "__mixed__";
const UNSET_MODEL_VALUE = "__unset__";

const isFileLike = (value: unknown): boolean => {
  if (!value || typeof value !== "object") {
    return false;
  }
  if (typeof File !== "undefined" && value instanceof File) {
    return true;
  }
  if (typeof Blob !== "undefined" && value instanceof Blob) {
    return true;
  }
  return false;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value) && !isFileLike(value);

const isUploadFileItem = (value: unknown): value is UploadFileItem => {
  if (!value || typeof value !== "object") {
    return false;
  }
  const candidate = value as UploadFileItem;
  return (
    Object.prototype.hasOwnProperty.call(candidate, "id") ||
    Object.prototype.hasOwnProperty.call(candidate, "file") ||
    Object.prototype.hasOwnProperty.call(candidate, "src") ||
    Object.prototype.hasOwnProperty.call(candidate, "dimensions")
  );
};

const cloneUploadFileItem = (item: UploadFileItem): UploadFileItem => ({
  ...item,
  dimensions: item.dimensions ? { ...item.dimensions } : undefined,
});

const cloneSchemaValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((entry) => cloneSchemaValue(entry));
  }
  if (isUploadFileItem(value)) {
    return cloneUploadFileItem(value as UploadFileItem);
  }
  if (isPlainObject(value)) {
    const next: Record<string, unknown> = {};
    Object.entries(value).forEach(([key, entry]) => {
      next[key] = cloneSchemaValue(entry);
    });
    return next;
  }
  return value;
};

const cloneSchemaValues = (values: SchemaFormValues<string>): SchemaFormValues<string> => {
  const next: SchemaFormValues<string> = {} as SchemaFormValues<string>;
  Object.entries(values ?? {}).forEach(([key, entry]) => {
    next[key as string] = cloneSchemaValue(entry);
  });
  return next;
};

const buildSeedValues = (
  schema: SchemaFormDefinition<string>,
  stored: SchemaFormValues<string> | null | undefined,
  prompt: string
): SchemaFormValues<string> => {
  const defaults = deriveInitialValues(schema);
  const seeded: SchemaFormValues<string> = { ...defaults };
  if (stored && typeof stored === "object") {
    Object.entries(stored).forEach(([key, value]) => {
      seeded[key as string] = cloneSchemaValue(value);
    });
  }
  const promptKey = schema.prompt?.primaryField;
  if (promptKey) {
    seeded[promptKey as string] = prompt ?? "";
  }
  return seeded;
};

const collectImagesFromSchemaValues = (
  schema: SchemaFormDefinition<string>,
  values: SchemaFormValues<string> | null | undefined
): UploadFileItem[] => {
  if (!schema || !values) {
    return [];
  }
  const collected: UploadFileItem[] = [];
  const record = values as Record<string, unknown>;
  traverseFields(schema.fields, (field) => {
    if (!isDataField(field) || field.type !== "imageUpload") {
      return;
    }
    const raw = record[field.key];
    if (!Array.isArray(raw)) {
      return;
    }
    raw.forEach((item) => {
      if (isUploadFileItem(item)) {
        collected.push(cloneUploadFileItem(item));
      }
    });
  });
  return collected;
};

const areValuesEqual = (left: unknown, right: unknown): boolean => {
  if (left === right) {
    return true;
  }
  if (Array.isArray(left) && Array.isArray(right)) {
    if (left.length !== right.length) {
      return false;
    }
    for (let i = 0; i < left.length; i += 1) {
      if (!areValuesEqual(left[i], right[i])) {
        return false;
      }
    }
    return true;
  }
  if (isUploadFileItem(left) && isUploadFileItem(right)) {
    return (
      left.id === right.id &&
      left.src === right.src &&
      left.file === right.file &&
      left.dimensions?.width === right.dimensions?.width &&
      left.dimensions?.height === right.dimensions?.height
    );
  }
  if (isPlainObject(left) && isPlainObject(right)) {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) {
      return false;
    }
    for (const key of leftKeys) {
      if (!areValuesEqual(left[key], right[key])) {
        return false;
      }
    }
    return true;
  }
  return false;
};

const areSchemaValuesEqual = (
  left: SchemaFormValues<string> | null,
  right: SchemaFormValues<string> | null
): boolean => {
  if (!left || !right) {
    return left === right;
  }
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) {
    return false;
  }
  for (const key of leftKeys) {
    if (!areValuesEqual(left[key], right[key])) {
      return false;
    }
  }
  return true;
};

const BatchTaskDetailPanel: React.FC = () => {
  const {
    tasks,
    selectedTaskIds,
    removeTasks,
    updateTasksByIds,
  } = useBatchTasks();

  const selectedTasks = useMemo(
    () => tasks.filter((task) => selectedTaskIds.includes(task.id)),
    [tasks, selectedTaskIds]
  );
  const selectedCount = selectedTasks.length;
  const [modelOptions, setModelOptions] = useState<BatchModelOption[]>(() => listBatchModelOptions());
  const [modelOptionsLoading, setModelOptionsLoading] = useState(() => listBatchModelOptions().length === 0);

  useEffect(() => {
    let cancelled = false;

    const loadModelOptions = async () => {
      try {
        await initAutoDiscovery();
        if (cancelled) return;
        setModelOptions(listBatchModelOptions());
      } catch {
        if (cancelled) return;
        setModelOptions([]);
      } finally {
        if (!cancelled) {
          setModelOptionsLoading(false);
        }
      }
    };

    void loadModelOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectionModelKey = useMemo(() => {
    if (!selectedTasks.length) {
      return UNSET_MODEL_VALUE;
    }
    const firstKey = selectedTasks[0].modelKey ?? null;
    if (!firstKey) {
      const anyAssigned = selectedTasks.some((task) => task.modelKey);
      return anyAssigned ? MIXED_MODEL_VALUE : UNSET_MODEL_VALUE;
    }
    const allSame = selectedTasks.every((task) => task.modelKey === firstKey);
    return allSame ? firstKey : MIXED_MODEL_VALUE;
  }, [selectedTasks]);

  const activeModelOption = useMemo(() => {
    if (
      !selectionModelKey ||
      selectionModelKey === MIXED_MODEL_VALUE ||
      selectionModelKey === UNSET_MODEL_VALUE
    ) {
      return null;
    }
    const option = modelOptions.find((item) => item.key === selectionModelKey);
    if (!option) {
      return null;
    }
    return isBatchSupportedModel(option.config) ? option : null;
  }, [selectionModelKey, modelOptions]);

  const activeModelKey = activeModelOption?.key ?? null;
  const activeConfig = activeModelOption?.config ?? null;

  const primaryTask = selectedTasks[0] ?? null;

  const [schema, setSchema] = useState<SchemaFormDefinition<string> | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [formSeedValues, setFormSeedValues] = useState<SchemaFormValues<string> | null>(null);
  const [formDraft, setFormDraft] = useState<SchemaFormValues<string> | null>(null);
  const [formDirty, setFormDirty] = useState(false);
  const [schemaInstanceKey, setSchemaInstanceKey] = useState(0);
  const formBaselineRef = useRef<SchemaFormValues<string> | null>(null);
  const awaitingInitialValuesRef = useRef(false);
  const formSeedRef = useRef<SchemaFormValues<string> | null>(null);

  const beginSeedCycle = useCallback(
    (seed: SchemaFormValues<string> | null) => {
      const prev = formSeedRef.current;
      const unchanged =
        (prev === null && seed === null) ||
        (prev !== null && seed !== null && areSchemaValuesEqual(prev, seed));
      if (unchanged) {
        if (!seed) {
          setFormDraft(null);
          formBaselineRef.current = null;
          awaitingInitialValuesRef.current = false;
          setFormDirty(false);
        }
        return;
      }
      formSeedRef.current = seed;
      setFormSeedValues(seed);
      setFormDraft(seed);
      formBaselineRef.current = null;
      awaitingInitialValuesRef.current = Boolean(seed);
      setFormDirty(false);
      setSchemaInstanceKey((value) => value + 1);
    },
    []
  );

  useEffect(() => {
    formSeedRef.current = formSeedValues;
  }, [formSeedValues]);

  useEffect(() => {
    let cancelled = false;
    if (!activeConfig) {
      setSchema(null);
      setSchemaError(null);
      setSchemaLoading(false);
      beginSeedCycle(null);
      return () => {
        cancelled = true;
      };
    }
    setSchemaLoading(true);
    setSchemaError(null);
    loadWorkspaceSchema(activeConfig)
      .then((definition) => {
        if (cancelled) return;
        setSchema(definition);
        setSchemaLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setSchema(null);
        setSchemaLoading(false);
        setSchemaError(error instanceof Error ? error.message : String(error));
      });
    return () => {
      cancelled = true;
    };
  }, [activeConfig, beginSeedCycle]);

  useEffect(() => {
    if (!schema || !activeModelKey || !primaryTask) {
      beginSeedCycle(null);
      return;
    }
    const prompt = primaryTask.prompt ?? "";
    const storedValues = primaryTask.formValues?.[activeModelKey] as SchemaFormValues<string> | undefined;
    const seed = buildSeedValues(schema, storedValues, prompt);
    beginSeedCycle(seed);
  }, [schema, activeModelKey, primaryTask?.id, primaryTask?.prompt, beginSeedCycle]);

  useEffect(() => {
    if (!schema || !activeModelKey || !selectedTaskIds.length) {
      return;
    }
    updateTasksByIds(selectedTaskIds, (task) => {
      if (task.modelKey !== activeModelKey) {
        return task;
      }
      const existing = task.formValues[activeModelKey];
      if (existing !== undefined && existing !== null) {
        return task;
      }
      const seed = buildSeedValues(schema, null, task.prompt ?? "");
      const images = collectImagesFromSchemaValues(schema, seed);
      return {
        ...task,
        formValues: {
          ...task.formValues,
          [activeModelKey]: seed,
        },
        images,
      };
    });
  }, [schema, activeModelKey, selectedTaskIds, updateTasksByIds]);

  const effectiveSchema = useMemo(() => {
    if (!schema) {
      return null;
    }
    if (!formSeedValues) {
      return schema;
    }
    return {
      ...schema,
      defaults: {
        ...(schema.defaults ?? {}),
        ...formSeedValues,
      },
    } as SchemaFormDefinition<string>;
  }, [schema, formSeedValues]);

  const handleRemoveSelected = () => {
    if (!selectedTaskIds.length) return;
    removeTasks(selectedTaskIds);
  };

  const handleModelChange = (raw: string) => {
    if (!selectedTaskIds.length) return;
    if (raw === UNSET_MODEL_VALUE) {
      updateTasksByIds(selectedTaskIds, (task) => ({
        ...task,
        modelKey: null,
        images: [],
      }));
      beginSeedCycle(null);
      setSchema(null);
      setSchemaError(null);
      setSchemaLoading(false);
      return;
    }
    const nextOption = modelOptions.find((item) => item.key === raw);
    if (!nextOption || !isBatchSupportedModel(nextOption.config)) return;
    const nextKey = nextOption.key;
    updateTasksByIds(selectedTaskIds, (task) => ({
      ...task,
      modelKey: nextKey,
      images: [],
    }));
    beginSeedCycle(null);
    setSchema(null);
    setSchemaError(null);
    setSchemaLoading(true);
  };

  const handleSchemaValuesChange = useCallback(
    (nextValues: SchemaFormValues<string>) => {
      setFormDraft(nextValues);
      if (awaitingInitialValuesRef.current) {
        awaitingInitialValuesRef.current = false;
        const baseline = cloneSchemaValues(nextValues);
        formBaselineRef.current = baseline;
        setFormDirty(false);
        return;
      }
      const baseline = formBaselineRef.current;
      if (!baseline) {
        setFormDirty(true);
        return;
      }
      const changed = !areSchemaValuesEqual(baseline, nextValues);
      setFormDirty(changed);
    },
    []
  );

  const handleApplyParameters = () => {
    if (!activeModelKey || !schema || !formDraft || !selectedTaskIds.length) return;

    updateTasksByIds(selectedTaskIds, (task) => {
      const cloned = cloneSchemaValues(formDraft);
      const promptKey = schema.prompt?.primaryField;
      if (promptKey) {
        cloned[promptKey as string] = task.prompt ?? "";
      }
      const nextImages = collectImagesFromSchemaValues(schema, cloned);
      return {
        ...task,
        modelKey: activeModelKey,
        formValues: {
          ...task.formValues,
          [activeModelKey]: cloned,
        },
        images: nextImages,
      };
    });
    formBaselineRef.current = cloneSchemaValues(formDraft);
    formSeedRef.current = cloneSchemaValues(formDraft);
    setFormDirty(false);
  };

  const handleResetToDefaults = () => {
    if (!schema || !activeModelKey || !primaryTask) {
      return;
    }
    const prompt = primaryTask.prompt ?? "";
    const defaults = buildSeedValues(schema, null, prompt);
    beginSeedCycle(defaults);
  };

  const renderModelForm = () => {
    if (!activeModelKey) {
      return null;
    }
    if (schemaError) {
      return (
        <Alert>
          <AlertDescription>{schemaError}</AlertDescription>
        </Alert>
      );
    }
    if (schemaLoading || !effectiveSchema || !formSeedValues) {
      return (
        <div className="flex justify-center py-4">
          {schemaLoading ? (
            <div className="animate-spin size-6 border-2 border-muted-foreground/50 border-t-foreground rounded-full" />
          ) : (
            <div className="text-sm text-muted-foreground">正在准备表单...</div>
          )}
        </div>
      );
    }
    return (
      <SchemaFormRenderer
        key={`${activeModelKey}-${schemaInstanceKey}`}
        schema={effectiveSchema}
        modelKey={activeModelKey}
        hideSubmit
        onValuesChange={handleSchemaValuesChange}
      />
    );
  };

  if (!selectedCount) {
    return (
      <div className="flex-1 min-h-0 p-3 md:p-4">
        <Alert>
          <AlertDescription>请选择左侧列表中的任务，以查看并配置批量参数。</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 p-3 md:p-4 overflow-y-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <div className="text-lg font-semibold">批量配置</div>
          <div className="text-sm text-muted-foreground">已选择 {selectedCount} 个任务。以下操作会同步作用于所有选中的任务。</div>
        </div>
        <Button variant="destructive" onClick={handleRemoveSelected}>删除选中</Button>
      </div>

      <Separator className="my-3" />

      <div className="grid gap-3">
        <div className="grid gap-1.5">
          <UiSelect value={selectionModelKey} onValueChange={handleModelChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="生成方案" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>生成方案</SelectLabel>
                <SelectItem value={UNSET_MODEL_VALUE}>
                  <em className="not-italic text-muted-foreground">未选择</em>
                </SelectItem>
                <SelectItem value={MIXED_MODEL_VALUE} disabled>
                  多种方案（保留现状）
                </SelectItem>
                {modelOptionsLoading ? (
                  <SelectItem value="__loading__" disabled>
                    正在加载方案...
                  </SelectItem>
                ) : modelOptions.length === 0 ? (
                  <SelectItem value="__empty__" disabled>
                    暂无可用方案
                  </SelectItem>
                ) : (
                  modelOptions.map((option) => (
                    <SelectItem key={option.key} value={option.key}>
                      {option.label}
                    </SelectItem>
                  ))
                )}
              </SelectGroup>
            </SelectContent>
          </UiSelect>

          {selectionModelKey === MIXED_MODEL_VALUE ? (
            <Alert>
              <AlertDescription>当前选中任务的生成方案不一致，请先统一选择方案再配置参数。</AlertDescription>
            </Alert>
          ) : null}
        </div>

        {activeModelKey ? (
          <div className="grid gap-3">
            {primaryTask ? (
              <div className="text-xs text-muted-foreground">
                参数表单基于选中列表中的第一个任务（#{primaryTask.meta?.csvLine ?? primaryTask.prompt.slice(0, 8)}），应用后会同步更新所有选中任务。
              </div>
            ) : null}

            {renderModelForm()}

            <div className="flex items-center justify-end gap-2.5">
              <Button
                variant="outline"
                onClick={handleResetToDefaults}
                disabled={!schema || !activeModelKey || !primaryTask || schemaLoading}
              >
                恢复默认值
              </Button>
              <Button
                onClick={handleApplyParameters}
                disabled={!schema || !formDraft || !formDirty || schemaLoading}
              >
                应用到选中任务
              </Button>
            </div>
          </div>
        ) : null}

        {!activeModelKey && selectionModelKey !== MIXED_MODEL_VALUE ? (
          <Alert>
            <AlertDescription>尚未为选中任务选择生成方案，请先在上方选择后再配置参数。</AlertDescription>
          </Alert>
        ) : null}
      </div>
    </div>
  );
};

export default BatchTaskDetailPanel;
